// Lightweight API helper for Family endpoints.
import axios from "axios";
import { API } from "@/lib/budget";

const TOKEN_KEY = "mysavio:parent-token";
const TOKEN_TS_KEY = "mysavio:parent-token-ts";
const SESSION_MS = 5 * 60 * 1000;

export const familyAPI = {
  // ----- Token helpers (local) -----
  getToken() {
    const t = localStorage.getItem(TOKEN_KEY);
    const ts = Number(localStorage.getItem(TOKEN_TS_KEY) || 0);
    if (!t || !ts) return null;
    if (Date.now() - ts > SESSION_MS) {
      this.clearToken();
      return null;
    }
    return t;
  },
  setToken(t) {
    localStorage.setItem(TOKEN_KEY, t);
    localStorage.setItem(TOKEN_TS_KEY, String(Date.now()));
  },
  touchToken() {
    if (localStorage.getItem(TOKEN_KEY)) {
      localStorage.setItem(TOKEN_TS_KEY, String(Date.now()));
    }
  },
  clearToken() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_TS_KEY);
  },

  _headers() {
    const t = this.getToken();
    return t ? { "X-Parent-Token": t } : {};
  },

  // ----- Public (kid) endpoints -----
  pinStatus: () => axios.get(`${API}/family/pin/status`).then((r) => r.data),
  getSettingsKid: () => axios.get(`${API}/family/settings`).then((r) => r.data),
  listNotes: (unreadOnly = false) =>
    axios.get(`${API}/family/notes`, { params: { unread_only: unreadOnly } }).then((r) => r.data),
  markNoteRead: (id) => axios.post(`${API}/family/notes/${id}/read`).then((r) => r.data),
  listApprovals: (status) =>
    axios.get(`${API}/family/approvals`, { params: status ? { status } : {} }).then((r) => r.data),
  createApproval: (body) => axios.post(`${API}/family/approvals`, body).then((r) => r.data),

  // ----- PIN flows -----
  async setPin(body) {
    const { data } = await axios.post(`${API}/family/pin/set`, body);
    this.setToken(data.token);
    return data;
  },
  async verifyPin(pin) {
    const { data } = await axios.post(`${API}/family/pin/verify`, { pin });
    this.setToken(data.token);
    return data;
  },
  async changePin(oldPin, newPin) {
    const { data } = await axios.post(`${API}/family/pin/change`, { old_pin: oldPin, new_pin: newPin });
    this.setToken(data.token);
    return data;
  },
  async logout() {
    try {
      await axios.post(`${API}/family/pin/logout`, {}, { headers: this._headers() });
    } catch (_) { /* ignore */ }
    this.clearToken();
  },

  // ----- Parent-only endpoints -----
  getSettingsParent: function () {
    return axios.get(`${API}/family/parent/settings`, { headers: this._headers() }).then((r) => { this.touchToken(); return r.data; });
  },
  updateSettings: function (body) {
    return axios.post(`${API}/family/parent/settings`, body, { headers: this._headers() }).then((r) => { this.touchToken(); return r.data; });
  },
  addNote: function (text) {
    return axios.post(`${API}/family/parent/notes`, { text }, { headers: this._headers() }).then((r) => { this.touchToken(); return r.data; });
  },
  deleteNote: function (id) {
    return axios.delete(`${API}/family/parent/notes/${id}`, { headers: this._headers() }).then((r) => { this.touchToken(); return r.data; });
  },
  setGoalBoost: function (month, amount) {
    return axios.post(`${API}/family/parent/goal-boost`, { month, amount }, { headers: this._headers() }).then((r) => { this.touchToken(); return r.data; });
  },
  resolveApproval: function (id, decision, parentNote) {
    return axios.post(`${API}/family/parent/approvals/${id}/resolve`,
      { decision, parent_note: parentNote || null },
      { headers: this._headers() }
    ).then((r) => { this.touchToken(); return r.data; });
  },
  getSummary: function (month) {
    return axios.get(`${API}/family/parent/summary`, {
      params: { month }, headers: this._headers(),
    }).then((r) => { this.touchToken(); return r.data; });
  },
};
